import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const NeedsWantsScenario = () => {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [sortedItems, setSortedItems] = useState({
    needs: [],
    wants: []
  })
  const [selectedPriorities, setSelectedPriorities] = useState([])
  const [step6Validated, setStep6Validated] = useState(false)
  const [step6Errors, setStep6Errors] = useState([])
  const [step9Validated, setStep9Validated] = useState(false)

  const allItems = [
    { id: 1, name: 'Chlieb', emoji: '🍞', type: 'need' },
    { id: 2, name: 'Zimná bunda', emoji: '🧥', type: 'need' },
    { id: 3, name: 'Voda', emoji: '💧', type: 'need' },
    { id: 4, name: 'Čokoláda', emoji: '🍫', type: 'want' },
    { id: 5, name: 'Hračka', emoji: '🧸', type: 'want' },
    { id: 6, name: 'Školské zošity', emoji: '📓', type: 'need' },
    { id: 7, name: 'Videohra', emoji: '🎮', type: 'want' },
    { id: 8, name: 'Zubná pasta', emoji: '🦷', type: 'need' }
  ]

  const toggleItemSort = (item, category) => {
    setSortedItems(prev => {
      const newNeeds = prev.needs.filter(i => i.id !== item.id)
      const newWants = prev.wants.filter(i => i.id !== item.id)
      
      if (category === 'needs') {
        return { needs: [...newNeeds, item], wants: newWants }
      } else {
        return { needs: newNeeds, wants: [...newWants, item] }
      }
    })
  }

  const togglePriority = (item) => {
    setSelectedPriorities(prev => {
      if (prev.find(i => i.id === item.id)) {
        return prev.filter(i => i.id !== item.id)
      } else if (prev.length < 3) {
        return [...prev, item]
      }
      return prev
    })
  }

  const validateStep6Sorting = () => {
    const errors = []
    
    // Check needs that are incorrectly placed in wants
    sortedItems.wants.forEach(item => {
      const correctItem = allItems.find(i => i.id === item.id)
      if (correctItem && correctItem.type === 'need') {
        errors.push({
          id: item.id,
          name: item.name,
          message: getFeedbackMessage(item.name, 'need', 'want')
        })
      }
    })
    
    // Check wants that are incorrectly placed in needs
    sortedItems.needs.forEach(item => {
      const correctItem = allItems.find(i => i.id === item.id)
      if (correctItem && correctItem.type === 'want') {
        errors.push({
          id: item.id,
          name: item.name,
          message: getFeedbackMessage(item.name, 'want', 'need')
        })
      }
    })
    
    setStep6Errors(errors)
    
    if (errors.length === 0) {
      setStep6Validated(true)
    }
    
    return errors.length === 0
  }

  const getFeedbackMessage = (itemName, correctType, placedIn) => {
    const feedbackMap = {
      'Hračka': 'Hračka môže byť príjemná, ale väčšinou patrí medzi želania.',
      'Čokoláda': 'Čokoláda je sladkosť a patrí medzi želania, nie medzi potreby.',
      'Videohra': 'Videohra je zábava a patrí medzi želania, nie medzi potreby.',
      'Chlieb': 'Chlieb je jedlo a patrí medzi potreby, lebo ho potrebujeme na každodennú stravu.',
      'Zimná bunda': 'Zimná bunda patrí medzi potreby, lebo potrebujeme oblečenie na ochranu pred chladom.',
      'Voda': 'Voda patrí medzi potreby, lebo ju potrebujeme na pitie každý deň.',
      'Školské zošity': 'Školské zošity patria medzi potreby, lebo ich potrebujeme na učenie.',
      'Zubná pasta': 'Zubná pasta patrí medzi potreby, lebo ju potrebujeme na hygienu.'
    }
    
    return feedbackMap[itemName] || `${itemName} patrí do inej skupiny.`
  }

  const getStep9Feedback = () => {
    // Count how many needs vs wants were selected
    const selectedNeeds = selectedPriorities.filter(item => {
      const correctItem = allItems.find(i => i.id === item.id)
      return correctItem && correctItem.type === 'need'
    })
    
    const selectedWants = selectedPriorities.filter(item => {
      const correctItem = allItems.find(i => i.id === item.id)
      return correctItem && correctItem.type === 'want'
    })
    
    // CASE A: Only essentials (3 needs or 2 needs + 1 want that's reasonable)
    if (selectedNeeds.length === 3) {
      return {
        type: 'positive',
        message: 'Dobré rozhodnutie. Najprv treba kúpiť to, čo rodina naozaj potrebuje.'
      }
    }
    
    // CASE B: Mix (2 needs + 1 want)
    if (selectedNeeds.length === 2 && selectedWants.length === 1) {
      return {
        type: 'reflective',
        message: 'Vybral si aj vec, ktorá nie je nevyhnutná. Keď máme málo peňazí, je dobré najprv kúpiť to, čo rodina naozaj potrebuje. Porozmýšľaj, či by si túto vec kúpil aj v takejto situácii.'
      }
    }
    
    // CASE C: Mostly wants (1 need + 2 wants, or 0 needs + 3 wants)
    return {
      type: 'strong-reflection',
      message: 'Zdá sa, že si vybral veci, ktoré rodina teraz nutne nepotrebuje. Skús sa zamyslieť, čo by bolo najdôležitejšie kúpiť ako prvé.'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-500 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-purple-100 mb-1">Interaktívny scenár</div>
                <h1 className="text-3xl font-bold">🛍️ Potreby vs. želania</h1>
              </div>
              {currentStep === 1 ? (
                <button
                  onClick={() => navigate('/scenarios')}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition text-sm font-medium"
                >
                  ← Späť na ročníky
                </button>
              ) : (
                <button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition text-sm font-medium"
                >
                  ← Späť
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Progress indicator */}
      <div className="bg-white border-b border-gray-200 py-3">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-2">
              {[...Array(12)].map((_, idx) => (
                <div
                  key={idx + 1}
                  className={`flex-1 h-2 rounded-full transition-all ${
                    idx + 1 <= currentStep ? 'bg-purple-500' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            <p className="text-sm text-gray-600 mt-2 text-center">
              Krok {currentStep} z 12
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">

          {/* Intro Slide 1: Question */}
          {currentStep === 1 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="text-6xl mb-4">💭</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Čo si musíme kúpiť a čo len chceme?
                </h2>
              </div>

              <div className="space-y-4 max-w-2xl mx-auto">
                <div className="p-6 bg-blue-50 rounded-xl border-2 border-blue-200">
                  <div className="font-bold text-gray-800 mb-2">A) Všetko, čo vidíme v obchode</div>
                  <p className="text-sm text-gray-600">Kúpime si čokoľvek sa nám páči</p>
                </div>
                
                <div className="p-6 bg-green-50 rounded-xl border-2 border-green-200">
                  <div className="font-bold text-gray-800 mb-2">B) Len to, čo naozaj potrebujeme</div>
                  <p className="text-sm text-gray-600">Premýšľame, či to potrebujeme alebo len chceme</p>
                </div>
              </div>

              <div className="text-center mt-8">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="px-8 py-4 bg-purple-600 text-white rounded-xl font-bold text-lg hover:bg-purple-700 transition"
                >
                  Pokračovať
                </button>
              </div>
            </div>
          )}

          {/* Intro Slide 2: Discussion */}
          {currentStep === 2 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-6">
                <div className="text-5xl mb-4">💬</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Diskusia v triede
                </h2>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-lg max-w-2xl mx-auto">
                <h3 className="font-bold text-gray-900 mb-4">Zamyslite sa:</h3>
                <ul className="space-y-3 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold mt-1">•</span>
                    <span>Čo potrebuješ každý deň? (jedlo, voda, oblečenie...)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold mt-1">•</span>
                    <span>Čo by bolo fajn mať, ale nie je to nutné?</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold mt-1">•</span>
                    <span>Ako rozhodujú rodičia, čo kúpia v obchode?</span>
                  </li>
                </ul>
              </div>

              <div className="text-center mt-8">
                <button
                  onClick={() => setCurrentStep(3)}
                  className="px-8 py-4 bg-purple-600 text-white rounded-xl font-bold text-lg hover:bg-purple-700 transition"
                >
                  Pokračovať
                </button>
              </div>
            </div>
          )}

          {/* Intro Slide 3: Explanation */}
          {currentStep === 3 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 text-center">
              <div className="text-6xl mb-6">💡</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Potreby a želania
              </h2>
              <div className="max-w-2xl mx-auto">
                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  <strong>Potreby</strong> sú veci, bez ktorých sa nezaobídeme – jedlo, voda, oblečenie, domov. Potrebujeme ich, aby sme boli zdraví a v bezpečí.
                </p>
                <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                  <strong>Želania</strong> sú veci, ktoré by boli fajn, ale nie sú nutné. Môžeme bez nich žiť. Nie všetko, čo chceme, je niečo, čo naozaj potrebujeme.
                </p>
              </div>
              <button
                onClick={() => setCurrentStep(4)}
                className="px-8 py-4 bg-purple-600 text-white rounded-xl font-bold text-lg hover:bg-purple-700 transition shadow-lg"
              >
                Začať scenár
              </button>
            </div>
          )}

          {/* Step 4: Introduction - Adam shopping */}
          {currentStep === 4 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">🛒</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Adam ide nakupovať
                </h2>
              </div>

              <div className="bg-purple-50 border-l-4 border-purple-400 p-6 rounded-lg mb-6">
                <p className="text-gray-700 mb-3">
                  Adam ide s mamou do obchodu. V košíku už majú:
                </p>
                <div className="flex gap-3 justify-center text-3xl mb-3">
                  🍞 🧈 🍎 🧼
                </div>
                <p className="text-gray-700 text-sm text-center">
                  Chlieb, maslo, ovocie a mydlo
                </p>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-lg mb-6">
                <p className="text-gray-700">
                  Mama hovorí: „Máme len <strong>30 eur</strong> na nákup. Musíme si dobre rozmyslieť, čo kúpime."
                </p>
              </div>

              <div className="text-center">
                <button
                  onClick={() => setCurrentStep(5)}
                  className="px-8 py-4 bg-purple-600 text-white rounded-xl font-bold text-lg hover:bg-purple-700 transition"
                >
                  Pokračovať
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Current cart display */}
          {currentStep === 5 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Čo už máme v košíku
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="p-4 bg-green-50 rounded-xl border-2 border-green-200 text-center">
                  <div className="text-4xl mb-2">🍞</div>
                  <div className="font-semibold text-gray-800">Chlieb</div>
                  <div className="text-xs text-gray-600 mt-1">2 €</div>
                </div>
                <div className="p-4 bg-green-50 rounded-xl border-2 border-green-200 text-center">
                  <div className="text-4xl mb-2">🧈</div>
                  <div className="font-semibold text-gray-800">Maslo</div>
                  <div className="text-xs text-gray-600 mt-1">3 €</div>
                </div>
                <div className="p-4 bg-green-50 rounded-xl border-2 border-green-200 text-center">
                  <div className="text-4xl mb-2">🍎</div>
                  <div className="font-semibold text-gray-800">Ovocie</div>
                  <div className="text-xs text-gray-600 mt-1">5 €</div>
                </div>
                <div className="p-4 bg-green-50 rounded-xl border-2 border-green-200 text-center">
                  <div className="text-4xl mb-2">🧼</div>
                  <div className="font-semibold text-gray-800">Mydlo</div>
                  <div className="text-xs text-gray-600 mt-1">4 €</div>
                </div>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg mb-6">
                <p className="text-gray-700 text-sm">
                  💰 Spolu: <strong>14 €</strong> | Zostáva: <strong>16 €</strong>
                </p>
              </div>

              <div className="text-center">
                <button
                  onClick={() => setCurrentStep(6)}
                  className="px-8 py-4 bg-purple-600 text-white rounded-xl font-bold text-lg hover:bg-purple-700 transition"
                >
                  Pokračovať
                </button>
              </div>
            </div>
          )}

          {/* Step 6: Interactive - Sort items into Needs/Wants */}
          {currentStep === 6 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                Roztriediť: Potreby alebo želania?
              </h2>
              <p className="text-center text-gray-600 mb-8">
                Klikni na položku a vyber, či je to potreba alebo želanie
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                {allItems.map(item => {
                  const hasError = step6Errors.find(e => e.id === item.id)
                  return (
                    <div key={item.id} className="text-center">
                      <div className={`p-4 rounded-xl border-2 mb-2 ${
                        hasError 
                          ? 'bg-red-50 border-red-400' 
                          : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="text-4xl mb-2">{item.emoji}</div>
                        <div className="font-semibold text-gray-800 text-sm">{item.name}</div>
                        {hasError && (
                          <div className="text-xs text-red-600 mt-2">⚠️</div>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => {
                            toggleItemSort(item, 'needs')
                            setStep6Errors([])
                            setStep6Validated(false)
                          }}
                          className={`flex-1 px-2 py-1 text-xs rounded ${
                            sortedItems.needs.find(i => i.id === item.id)
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          Potreba
                        </button>
                        <button
                          onClick={() => {
                            toggleItemSort(item, 'wants')
                            setStep6Errors([])
                            setStep6Validated(false)
                          }}
                          className={`flex-1 px-2 py-1 text-xs rounded ${
                            sortedItems.wants.find(i => i.id === item.id)
                              ? 'bg-pink-500 text-white'
                              : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          Želanie
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>

              {step6Errors.length > 0 && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg mb-6">
                  <p className="font-bold text-gray-900 mb-3">Skús sa ešte raz zamyslieť. Niektoré veci patria do inej skupiny:</p>
                  <ul className="space-y-2">
                    {step6Errors.map(error => (
                      <li key={error.id} className="text-sm text-gray-700">
                        • <strong>{error.name}:</strong> {error.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {step6Validated && (
                <div className="bg-green-50 border-l-4 border-green-400 p-6 rounded-lg mb-6">
                  <p className="text-gray-700 font-bold">
                    ✅ Výborne, teraz už vieš rozlíšiť potreby a želania!
                  </p>
                </div>
              )}

              <div className="text-center space-y-3">
                {!step6Validated && (
                  <button
                    onClick={validateStep6Sorting}
                    disabled={sortedItems.needs.length + sortedItems.wants.length < 8}
                    className={`px-8 py-4 rounded-xl font-bold text-lg transition ${
                      sortedItems.needs.length + sortedItems.wants.length < 8
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {sortedItems.needs.length + sortedItems.wants.length < 8
                      ? `Roztried všetky položky (${sortedItems.needs.length + sortedItems.wants.length}/8)`
                      : 'Skontrolovať'}
                  </button>
                )}
                
                {step6Validated && (
                  <button
                    onClick={() => setCurrentStep(7)}
                    className="px-8 py-4 bg-purple-600 text-white rounded-xl font-bold text-lg hover:bg-purple-700 transition"
                  >
                    Pokračovať
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Step 7: Adam sees toy dinosaur */}
          {currentStep === 7 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">🦖</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Veľký dinosaurus!
                </h2>
              </div>

              <div className="bg-pink-50 border-l-4 border-pink-400 p-6 rounded-lg mb-6">
                <p className="text-gray-700 mb-3">
                  Adam vidí v obchode <strong>veľkého hračkárske dinosaura</strong>. Vyzerá super!
                </p>
                <p className="text-gray-700 mb-3">
                  „Mama, môžem ho? Prosím!" hovorí Adam.
                </p>
                <p className="text-gray-700">
                  Mama sa pýta: „Adamko, naozaj ho <strong>potrebujeme</strong>, alebo ho len <strong>chceme</strong>?"
                </p>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg mb-6">
                <p className="text-gray-700 text-sm">
                  💡 Dinosaurus stojí <strong>12 €</strong>. V košíku už máme za 14 €. Celkom by to bolo <strong>26 €</strong> (máme len 30 €, zostalo by 4 €).
                </p>
              </div>

              <div className="text-center">
                <button
                  onClick={() => setCurrentStep(8)}
                  className="px-8 py-4 bg-purple-600 text-white rounded-xl font-bold text-lg hover:bg-purple-700 transition"
                >
                  Pokračovať
                </button>
              </div>
            </div>
          )}

          {/* Step 8: Discussion - Need or Want? */}
          {currentStep === 8 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-6">
                <div className="text-5xl mb-4">💬</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Potreba alebo želanie?
                </h2>
              </div>

              <div className="bg-purple-50 border-l-4 border-purple-400 p-6 rounded-lg mb-6">
                <h3 className="font-bold text-gray-900 mb-3">Zamyslite sa:</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold mt-1">•</span>
                    <span>Môže Adam žiť bez hračky dinosaura?</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold mt-1">•</span>
                    <span>Je dinosaurus niečo, čo Adam <strong>nutne</strong> potrebuje, alebo len chce?</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold mt-1">•</span>
                    <span>Čo by sa stalo, keby si ho kúpili a potom zistili, že im chýbajú peniaze na niečo dôležité?</span>
                  </li>
                </ul>
              </div>

              <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-lg mb-6">
                <p className="text-gray-700 text-sm">
                  💡 <strong>Dôležité:</strong> Dinosaurus je želanie, nie potreba. Adam by si s ním mohol hrať, ale nepotrebuje ho na prežitie alebo zdravie.
                </p>
              </div>

              <div className="text-center">
                <button
                  onClick={() => setCurrentStep(9)}
                  className="px-8 py-4 bg-purple-600 text-white rounded-xl font-bold text-lg hover:bg-purple-700 transition"
                >
                  Pokračovať
                </button>
              </div>
            </div>
          )}

          {/* Step 9: Interactive - Choose 3 priority items */}
          {currentStep === 9 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                Čo kúpiť ako prvé?
              </h2>
              <p className="text-center text-gray-600 mb-6">
                Máme len 16 € navyše. Vyber <strong>3 veci</strong>, ktoré by mala rodina kúpiť ako prvé.
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {allItems.map(item => (
                  <button
                    key={item.id}
                    onClick={() => {
                      togglePriority(item)
                      setStep9Validated(false)
                    }}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      selectedPriorities.find(i => i.id === item.id)
                        ? 'bg-purple-100 border-purple-500 shadow-lg'
                        : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-4xl mb-2">{item.emoji}</div>
                    <div className="font-semibold text-gray-800 text-sm">{item.name}</div>
                    {selectedPriorities.find(i => i.id === item.id) && (
                      <div className="text-xs text-purple-600 font-bold mt-1">
                        ✓ Vybraté
                      </div>
                    )}
                  </button>
                ))}
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg mb-6">
                <p className="text-gray-700 text-sm">
                  Vybraté: <strong>{selectedPriorities.length} / 3</strong>
                </p>
              </div>

              {step9Validated && selectedPriorities.length === 3 && (() => {
                const feedback = getStep9Feedback()
                return (
                  <>
                    <div className={`border-l-4 p-6 rounded-lg mb-6 ${
                      feedback.type === 'positive' 
                        ? 'bg-green-50 border-green-400'
                        : feedback.type === 'reflective'
                        ? 'bg-blue-50 border-blue-400'
                        : 'bg-orange-50 border-orange-400'
                    }`}>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        {feedback.message}
                      </p>
                    </div>

                    <div className="bg-purple-50 border-l-4 border-purple-400 p-6 rounded-lg mb-6">
                      <p className="text-gray-700 text-sm">
                        💬 <strong>Diskusia v triede:</strong> Porozprávajte sa: Čo by ste kúpili ako prvé vy?
                      </p>
                    </div>
                  </>
                )
              })()}

              <div className="text-center">
                {!step9Validated ? (
                  <button
                    onClick={() => setStep9Validated(true)}
                    disabled={selectedPriorities.length !== 3}
                    className={`px-8 py-4 rounded-xl font-bold text-lg transition ${
                      selectedPriorities.length !== 3
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {selectedPriorities.length !== 3
                      ? `Vyber 3 veci (${selectedPriorities.length}/3)`
                      : 'Zobraziť spätnú väzbu'}
                  </button>
                ) : (
                  <button
                    onClick={() => setCurrentStep(10)}
                    className="px-8 py-4 bg-purple-600 text-white rounded-xl font-bold text-lg hover:bg-purple-700 transition"
                  >
                    Pokračovať
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Step 10: Consequence based on choices */}
          {currentStep === 10 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">✨</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Čo sa stalo
                </h2>
              </div>

              <div className="bg-green-50 border-l-4 border-green-400 p-6 rounded-lg mb-6">
                <h3 className="font-bold text-gray-900 mb-3">Tvoja voľba:</h3>
                <div className="flex gap-3 justify-center text-3xl mb-3">
                  {selectedPriorities.map(item => (
                    <span key={item.id}>{item.emoji}</span>
                  ))}
                </div>
                <p className="text-gray-700 text-center text-sm">
                  {selectedPriorities.map(i => i.name).join(', ')}
                </p>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-lg mb-6">
                <p className="text-gray-700 mb-3">
                  Mama povedala Adamovi: „Adamko, dinosaurus je krásny, ale teraz potrebujeme kúpiť veci, ktoré sú dôležitejšie."
                </p>
                <p className="text-gray-700 mb-3">
                  „Možno na narodeniny alebo keď ušetríme, si ho môžeš kúpiť. Teraz musíme myslieť na to, čo rodina naozaj potrebuje."
                </p>
                <p className="text-gray-700">
                  Adam pochopil. Nie všetko, čo chceme, si môžeme hneď kúpiť.
                </p>
              </div>

              <div className="text-center">
                <button
                  onClick={() => setCurrentStep(11)}
                  className="px-8 py-4 bg-purple-600 text-white rounded-xl font-bold text-lg hover:bg-purple-700 transition"
                >
                  Pokračovať
                </button>
              </div>
            </div>
          )}

          {/* Step 11: Reflection */}
          {currentStep === 11 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">🎓</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Čo si sa naučil?
                </h2>
              </div>

              <div className="space-y-4 max-w-2xl mx-auto mb-8">
                <div className="bg-green-50 border-l-4 border-green-400 p-5 rounded-lg">
                  <h3 className="font-bold text-gray-900 mb-2">1. Potreby</h3>
                  <p className="text-gray-700 text-sm">
                    Veci, bez ktorých sa nezaobídeme – jedlo, voda, oblečenie, domov, zdravie. Bez nich by sme nemohli žiť normálne.
                  </p>
                </div>

                <div className="bg-pink-50 border-l-4 border-pink-400 p-5 rounded-lg">
                  <h3 className="font-bold text-gray-900 mb-2">2. Želania</h3>
                  <p className="text-gray-700 text-sm">
                    Veci, ktoré by boli fajn, ale nie sú nutné – hračky, sladkosti, videohry. Môžeme bez nich žiť, aj keď by bolo príjemné ich mať.
                  </p>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-400 p-5 rounded-lg">
                  <h3 className="font-bold text-gray-900 mb-2">3. Rozhodovanie</h3>
                  <p className="text-gray-700 text-sm">
                    Keď máme obmedzené peniaze, musíme si vybrať. Najprv kúpime to, čo potrebujeme. Želania môžu počkať alebo ich môžeme ušetriť.
                  </p>
                </div>
              </div>

              <div className="text-center">
                <button
                  onClick={() => setCurrentStep(12)}
                  className="px-8 py-4 bg-purple-600 text-white rounded-xl font-bold text-lg hover:bg-purple-700 transition"
                >
                  Ukončiť
                </button>
              </div>
            </div>
          )}

          {/* Step 12: Final discussion */}
          {currentStep === 12 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 text-center">
              <div className="text-6xl mb-6">🎉</div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Scenár dokončený!
              </h2>

              <div className="bg-purple-50 border-l-4 border-purple-400 p-6 rounded-lg max-w-2xl mx-auto mb-8">
                <h3 className="font-bold text-gray-900 mb-4">💬 Diskusia do triedy:</h3>
                <ul className="space-y-3 text-sm text-gray-700 text-left">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold mt-1">•</span>
                    <span>Ktoré z vašich vecí doma sú potreby a ktoré želania?</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold mt-1">•</span>
                    <span>Je v poriadku mať želania? (Áno! Ale musíme rozlišovať medzi potrebami a želaniami.)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold mt-1">•</span>
                    <span>Ako môžete ušetriť na niečo, čo chcete?</span>
                  </li>
                </ul>
              </div>

              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => {
                    setCurrentStep(1)
                    setSortedItems({ needs: [], wants: [] })
                    setSelectedPriorities([])
                  }}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition"
                >
                  🔄 Skúsiť znova
                </button>
                <button
                  onClick={() => navigate('/scenarios')}
                  className="px-6 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition"
                >
                  Späť na ročníky
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}

export default NeedsWantsScenario
